/**
 * Provider-agnostic auth hook (MOD-885 tranche 5).
 *
 * Returns the same shape regardless of whether the build is using Kinde,
 * Auth0 (cloud), or Keycloak (on-prem), and whether it's running in a
 * browser tab or a Tauri webview. Lets downstream UI render against one
 * contract instead of branching on the auth backend.
 *
 * ## Dispatch
 *
 *                    │  Web (Next.js)         │  Tauri (desktop)
 * ───────────────────┼────────────────────────┼─────────────────────────────────
 *  Kinde (cloud)     │  useKindeBrowserClient │  useKindeAuth (react)
 *  Auth0 (cloud)     │  useAuth0Session       │  useDesktopAuthSafe (native)
 *  Keycloak (on-prem)│  useSession (NextAuth) │  useKeycloakTauri
 *
 * The top-level dispatch keys are `isKeycloak` (from `buildProfile`) and
 * `isTauri()` — both build-profile constants, stable for the lifetime of
 * the app. Within the cloud branch, all three cloud providers (Kinde,
 * Auth0 web, Auth0 desktop) coexist on the same bundle and the active
 * provider is decided per-user: `useAuthCloudPath` calls all three
 * underlying hooks and returns the first one that surfaced an access
 * token. Each hook self-gates so the others are cheap no-ops (see the
 * `useAuthCloudPath` JSDoc for details). Precedence is
 * AUTH0_DESKTOP > AUTH0 web > Kinde — same order
 * `src/components/utils/auth.tsx` uses for its on-the-fly selection.
 *
 * ## Migration path
 *
 * Existing call sites use `useKinde()` from `src/hooks/useKinde.ts`,
 * which is hardcoded to the Kinde backend. They keep working for cloud
 * builds but break on on-prem because no Kinde provider exists in the
 * tree, and they return empty state for Auth0 users (those sites pair
 * `useKinde()` with `useAuth0Session()` and select on token presence —
 * see `src/components/utils/auth.tsx:117`).
 *
 * For T5 we ship this hook only — the ~15 consumers of `useKinde()`
 * migrate to `useAuth()` in follow-up tranches (T6 login UI, T7
 * middleware, T5.x cleanup PRs). The two coexist during the migration
 * window.
 *
 * ## Shape choices
 *
 * Token retrieval goes through `getToken(): Promise<string | null>` —
 * never a sync `accessToken` property. Async by design so a future
 * Keycloak / Auth0 SDK integration can refresh on demand (using the
 * refresh token) rather than handing back a stale localStorage
 * snapshot. Consumers that need a sync "is the user logged in?" answer
 * use `isAuthenticated`; consumers that need a token wrap their
 * fetching code in an effect and `await getToken()`.
 *
 * `provider` returns an `AuthProvider` enum value (KINDE / AUTH0 /
 * AUTH0_DESKTOP / KEYCLOAK), matching the same enum the Redux
 * `authModel` uses — so downstream code can compare against a single
 * symbol set rather than juggling string literals.
 *
 * `register()` semantics:
 * - **Kinde / Auth0**: self-service signup is supported; redirects to
 *   the provider's hosted signup screen.
 * - **Keycloak**: the on-prem realm disables self-registration
 *   (admin-only provisioning per MOD-877's "Email / SMS policy"
 *   decision); `register` logs a warning and is a no-op. UI that calls
 *   `register()` on on-prem should hide the CTA via `isKeycloak` at the
 *   call site rather than relying on the no-op.
 */
import { useCallback, useMemo } from 'react';

import type { UserProfile } from 'oidc-client-ts';

import type { useAuthKeycloakWebPath as UseAuthKeycloakWebPath } from './useAuthKeycloakWebPath';

import { useAuth0Session } from '@/hooks/useAuth0Session';
import { useKinde } from '@/hooks/useKinde';
import { useDesktopAuthSafe } from '@/tauri/auth0/desktop-auth-context';
import {
  clearKeycloakTauriSession,
  startKeycloakSignIn,
} from '@/tauri/keycloak/oidcClient';
import { useKeycloakTauri } from '@/tauri/keycloak/useKeycloakTauri';
import { isTauri } from '@/tauri/utils';
import { AuthProvider } from '@/utils/auth/persistedAuthProvider';
import { isKeycloak } from '@/utils/buildProfile';
import { navigateToOnPremDesktopRecovery } from '@/utils/onpremDesktopRecovery';
import { AUTH0_LOGIN_PATH } from '@heidi/auth/contracts';
import { extractUserRefIdFromAuth0Token } from '@heidi/auth/tokens';

export type AuthUser = {
  /**
   * Stable identifier for the user. Source per provider:
   * - **Kinde** — Kinde's `user.id` field (e.g. `kp_xxx`).
   * - **Auth0 web** — `user_ref_id` claim from the access token (Heidi
   *   canonical id), falling back to Auth0's `sub` (e.g. `auth0|xxx`).
   * - **Auth0 desktop (AUTH0_DESKTOP)** — `user_ref_id` decoded from
   *   the JWT in the native session (the Rust `auth_session` response
   *   doesn't carry profile claims directly).
   * - **Keycloak web** — the OIDC `sub` claim, enriched onto
   *   `session.user.id` by the NextAuth `session` callback in
   *   `authOptions.ts`.
   * - **Keycloak Tauri** — the OIDC `sub` claim from `User.profile`.
   *
   * May be `null` during loading or if the underlying SDK hasn't
   * surfaced a session yet.
   */
  id: string | null;
  email: string | null;
  givenName: string | null;
  familyName: string | null;
};

export type AuthState = {
  /** True once a session is confirmed; false during loading or unauthenticated. */
  isAuthenticated: boolean;
  /** True while the underlying provider is resolving the session. */
  isLoading: boolean;
  /** True when refresh has failed (Keycloak path) — UI should prompt re-login. */
  hasRefreshError: boolean;
  /** Parsed profile fields. Null when unauthenticated. */
  user: AuthUser | null;
  /**
   * Provider tag. Use sparingly; prefer behaviour-equivalence — but it's
   * useful when a UI surface needs to distinguish AUTH0 web from
   * AUTH0_DESKTOP, or to gate a Kinde-only CTA. Matches the
   * `AuthProvider` enum that `authModel`'s Redux state already uses.
   */
  provider: AuthProvider;
  /**
   * Returns the current access token. Async by design: future Keycloak
   * / Auth0 SDK integrations can refresh on demand here (using the
   * refresh token) rather than returning a stale localStorage snapshot.
   * Returns null when unauthenticated or before the initial session
   * resolves. Consumers that need a sync value should subscribe to
   * `isAuthenticated` and call `getToken()` from an effect.
   */
  getToken: () => Promise<string | null>;
  /**
   * Initiate the login flow. Kinde / Auth0 paths redirect to the
   * provider-hosted login screen; Keycloak paths redirect to the
   * configured Keycloak realm.
   */
  login: () => void;
  /** Initiate the logout flow. */
  logout: () => void;
  /**
   * Self-service signup. Supported on Kinde and Auth0; on Keycloak the
   * realm disables self-registration (admin-only provisioning per
   * MOD-877 policy) so `register` logs a warning and is a no-op.
   */
  register: () => void;
};

type KindeAuthShape = {
  isAuthenticated?: boolean;
  isLoading?: boolean;
  user?: {
    id?: string | null;
    email?: string | null;
    given_name?: string | null;
    family_name?: string | null;
  } | null;
  accessToken?: string | null;
  getToken?: () => Promise<string | undefined>;
  login?: (opts?: unknown) => void;
  logout?: (opts?: unknown) => void;
  register?: (opts?: unknown) => void;
};

function useAuthKindePath(): AuthState {
  const kinde = useKinde() as KindeAuthShape;
  // `useKinde()` returns a fresh object each render (both
  // `useKindeBrowserClient` and `useKindeAuth` spread their internal state
  // into a new literal), so deps must be the individual function refs —
  // not the wrapper object — for `useCallback` / `useMemo` to actually
  // memoize. Same idea drives the `useMemo` on `user` below: the inline
  // object would be a new reference every render and break shallow-equal
  // checks in any memoized child that receives `user` as a prop.
  const {
    user: kindeUser,
    accessToken: kindeAccessToken,
    getToken: kindeGetToken,
    login: kindeLogin,
    logout: kindeLogout,
    register: kindeRegister,
  } = kinde;

  const user = useMemo<AuthUser | null>(
    () =>
      kindeUser
        ? {
            id: kindeUser.id ?? null,
            email: kindeUser.email ?? null,
            givenName: kindeUser.given_name ?? null,
            familyName: kindeUser.family_name ?? null,
          }
        : null,
    [kindeUser]
  );
  const accessToken = kindeAccessToken ?? null;

  const getToken = useCallback(async (): Promise<string | null> => {
    if (kindeGetToken) {
      const tok = await kindeGetToken();
      return tok ?? null;
    }
    return accessToken;
  }, [kindeGetToken, accessToken]);

  const login = useCallback(() => {
    kindeLogin?.();
  }, [kindeLogin]);
  const logout = useCallback(() => {
    kindeLogout?.();
  }, [kindeLogout]);
  const register = useCallback(() => {
    kindeRegister?.();
  }, [kindeRegister]);

  return {
    isAuthenticated: Boolean(kinde.isAuthenticated),
    isLoading: Boolean(kinde.isLoading),
    hasRefreshError: false,
    user,
    provider: AuthProvider.KINDE,
    getToken,
    login,
    logout,
    register,
  };
}

function useAuthAuth0Path(): AuthState {
  const auth0 = useAuth0Session();

  const user = useMemo<AuthUser | null>(() => {
    if (!auth0.user) return null;
    // Auth0's user object exposes a single `name` field (per the OIDC
    // standard claims). Match the Keycloak-web path's split on first
    // space so consumers see consistent givenName / familyName shape.
    const name = auth0.user.name ?? '';
    const [given = null, ...rest] = name ? name.split(' ') : [];
    const family = rest.length > 0 ? rest.join(' ') : null;
    return {
      // Prefer the canonical Heidi `user_ref_id` claim (parsed from the
      // access token by useAuth0Session); fall back to Auth0's `sub` so
      // we never surface null when a session exists.
      id: auth0.userRefId ?? auth0.user.sub ?? null,
      email: auth0.user.email ?? null,
      givenName: given || null,
      familyName: family,
    };
  }, [auth0.user, auth0.userRefId]);

  const { accessToken } = auth0;

  const getToken = useCallback(() => Promise.resolve(accessToken), [accessToken]);

  const login = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.location.assign(AUTH0_LOGIN_PATH);
    }
  }, []);
  const logout = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.location.assign('/api/auth0/logout');
    }
  }, []);
  const register = useCallback(() => {
    if (typeof window !== 'undefined') {
      // Auth0 hosts signup behind the same /api/auth0/login endpoint
      // gated by the `screen_hint=signup` parameter — same pattern the
      // existing onboarding-v2 / sign-in modal flows use.
      window.location.assign(`${AUTH0_LOGIN_PATH}?screen_hint=signup`);
    }
  }, []);

  return {
    isAuthenticated: auth0.isAuthenticated,
    isLoading: auth0.isLoading,
    // Auth0 SDK surfaces refresh errors as thrown queries (auth0.error);
    // there's no separate refresh-error channel like NextAuth's
    // RefreshAccessTokenError. Leave false here and let consumers read
    // `auth0.error` directly if they need finer-grained handling.
    hasRefreshError: false,
    user,
    provider: AuthProvider.AUTH0,
    getToken,
    login,
    logout,
    register,
  };
}

function useAuthAuth0DesktopPath(): AuthState {
  // `useDesktopAuthSafe()` returns sentinel defaults when called outside
  // a `DesktopAuthProvider` (i.e. on web / on-prem-web builds), so it's
  // safe to call here unconditionally — the Tauri-only provider mounts
  // in `src/components/utils/tauriAuth.tsx` and the safe variant short-
  // circuits everywhere else.
  const {
    session,
    isAuthenticated,
    isLoading,
    login: desktopLogin,
    logout: desktopLogout,
  } = useDesktopAuthSafe();

  const accessToken = session?.accessToken ?? null;

  const user = useMemo<AuthUser | null>(() => {
    if (!accessToken) return null;
    // The Rust `auth_session` response carries
    // { isAuthenticated, expiresAt, hasRefreshToken, accessToken } —
    // it does NOT include profile claims. Surface the canonical
    // user_ref_id from the JWT so callers have a stable id; email /
    // givenName / familyName remain null until a follow-up wires
    // desktop user-info decoding (the JWT carries the claims; we just
    // don't have an extractor for them yet — same gap exists in the
    // web Auth0 path until /api/auth0-session responds, and the
    // desktop path has no analogous endpoint).
    const userRefId = extractUserRefIdFromAuth0Token(accessToken) ?? null;
    return {
      id: userRefId,
      email: null,
      givenName: null,
      familyName: null,
    };
  }, [accessToken]);

  const getToken = useCallback(() => Promise.resolve(accessToken), [accessToken]);

  const login = useCallback(() => {
    void desktopLogin();
  }, [desktopLogin]);
  const logout = useCallback(() => {
    void desktopLogout();
  }, [desktopLogout]);
  const register = useCallback(() => {
    // Desktop signup reuses the login flow with screenHint=signup, the
    // same hint launchDesktopAuthLogin() inspects to set the
    // signin/signup loginIntent in the desktopAuthSlice. Mirrors the
    // web pattern of /api/auth0/login?screen_hint=signup.
    void desktopLogin({ screenHint: 'signup' });
  }, [desktopLogin]);

  return {
    isAuthenticated,
    isLoading,
    hasRefreshError: false,
    user,
    // Distinguish desktop Auth0 from web Auth0 — same split the Redux
    // `authModel` uses for its own routing (e.g. desktop logout goes
    // through `/desktop-logout`, web goes through `/api/auth0/logout`).
    provider: AuthProvider.AUTH0_DESKTOP,
    getToken,
    login,
    logout,
    register,
  };
}

/**
 * Cloud dispatch: all three cloud paths fire on every cloud render and
 * the one with an access token wins. Each underlying hook self-gates so
 * the other two are cheap no-ops for users not on that provider:
 *
 * - `useDesktopAuthSafe()` returns sentinel defaults outside a
 *   `DesktopAuthProvider` (i.e. on web).
 * - `useAuth0Session()` gates its TanStack query on the `AUTH_PROVIDER`
 *   cookie + `!isTauri()`, so it only fires for confirmed web-Auth0
 *   users (or first visitors before the cookie is set).
 * - `useKinde()` is the existing always-on fallback (same SDK call the
 *   ~15 current `useKinde()` callsites already make every render).
 *
 * Precedence: AUTH0_DESKTOP > AUTH0 web > Kinde. Mirrors what
 * `src/components/utils/auth.tsx:117` does today (it only handles
 * AUTH0 web + Kinde; the desktop path is selected by the platform-
 * specific wrapper in `tauriAuth.tsx`).
 */
function useAuthCloudPath(): AuthState {
  const kindeState = useAuthKindePath();
  const auth0WebState = useAuthAuth0Path();
  const auth0DesktopState = useAuthAuth0DesktopPath();

  // Dispatch on `isAuthenticated` — each underlying SDK only reports true
  // when it has a valid token, so this is equivalent to the existing
  // `auth0Token || kindeToken` rule in `src/components/utils/auth.tsx`.
  // Token retrieval itself goes through `getToken()` so a future SDK
  // refresh path stays consistent across providers.
  if (auth0DesktopState.isAuthenticated) return auth0DesktopState;
  if (auth0WebState.isAuthenticated) return auth0WebState;
  return kindeState;
}

function useAuthKeycloakTauriPath(): AuthState {
  const state = useKeycloakTauri();
  const isLoading = state.status === 'loading';
  const isAuthenticated = state.status === 'authenticated';
  const hasRefreshError = state.status === 'error';
  const accessToken = state.status === 'authenticated' ? state.user.access_token : null;

  const user = useMemo<AuthUser | null>(() => {
    if (state.status !== 'authenticated') return null;
    // `state.user.profile` is already typed `UserProfile` by oidc-client-ts
    // (sub is required; email / given_name / family_name are optional per
    // the OIDC standard-claims spec). No cast needed.
    const profile: UserProfile = state.user.profile;
    return {
      id: profile.sub,
      email: profile.email ?? null,
      givenName: profile.given_name ?? null,
      familyName: profile.family_name ?? null,
    };
  }, [state]);

  const getToken = useCallback(() => Promise.resolve(accessToken), [accessToken]);
  const login = useCallback(() => {
    startKeycloakSignIn().catch((err: unknown) => {
      // Surface login failures so the UI isn't silently dead (the previous
      // `void` pattern swallowed redirect_uri mismatches, network errors, and
      // Keycloak 400s into an unhandled rejection that nothing observed).
      // eslint-disable-next-line no-console
      console.error('[useAuth] Keycloak sign-in failed:', err);
    });
  }, []);
  const logout = useCallback(() => {
    // NOT a full product logout: this path only clears the local OIDC session
    // and routes to recovery. The full sweep (persisted models, audio wipe,
    // analytics identity reset) lives in `dispatch.auth.logout()` — UI logout
    // surfaces must go through that, not this hook.
    void (async () => {
      try {
        await clearKeycloakTauriSession({ requireFreshLogin: true });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[useAuth] Keycloak local sign-out failed:', err);
      } finally {
        if (typeof window !== 'undefined') {
          navigateToOnPremDesktopRecovery('logout');
        }
      }
    })();
  }, []);
  const register = useCallback(() => {
    // eslint-disable-next-line no-console
    console.warn(
      '[useAuth] register() is a no-op on Keycloak — realm disables self-registration'
    );
  }, []);

  return {
    isAuthenticated,
    isLoading,
    hasRefreshError,
    user,
    provider: AuthProvider.KEYCLOAK,
    getToken,
    login,
    logout,
    register,
  };
}

/**
 * Public unified auth hook. Dispatches on `isKeycloak` + `isTauri()` to
 * the appropriate underlying provider.
 *
 * Only the active branch's hook is called — calling all three would fire
 * the unused branches' side effects, none of which are inert:
 *
 * - On a cloud build, `useAuthKeycloakWebPath()` would call NextAuth's
 *   `useSession()` outside any `<SessionProvider>` (mounted via
 *   `KeycloakAuthShell` only on Keycloak builds), which throws.
 * - On a cloud build, `useAuthKeycloakTauriPath()` would mount a
 *   `useEffect` that instantiates a `UserManager` with an empty issuer +
 *   client id (the `keycloakConfig` module returns empty strings on
 *   cloud builds).
 * - On a Keycloak web build, `useAuthCloudPath()` would call
 *   `useKindeBrowserClient`, which fires `fetch('/api/auth/setup')` —
 *   an endpoint that doesn't exist in on-prem deployments and is on the
 *   F1 forbidden-SDK list in `.ai-context/onprem-guardrails.md`.
 *
 * The cloud branch itself fans out to both Kinde and Auth0 — that fan-out
 * is intentional and matches what `src/components/utils/auth.tsx` does in
 * production today; see `useAuthCloudPath` for the precedence rule.
 *
 * `isKeycloak` (a build-profile constant from `buildProfile.ts`) and
 * `isTauri()` (derived from `window.__TAURI__` once at runtime startup)
 * are stable for the lifetime of the app, so the dispatched branch is
 * fixed across renders. Hook order therefore stays stable within any
 * single environment, even though it looks like a conditional hook call
 * to the `react-hooks/rules-of-hooks` rule.
 *
 * ## Why the Keycloak-web path uses `require()`
 *
 * `next-auth/react/index.js` runs `parseUrl(process.env.NEXTAUTH_URL)` at
 * module top level. Cloud builds don't set NEXTAUTH_URL, so a static
 * import of `next-auth/react` anywhere in a cloud server-rendered page
 * graph crashes SSR with `TypeError: Invalid URL`. By keeping the
 * Keycloak-web hook (and its `next-auth/react` imports) in a separate
 * module loaded via `require()` from inside the Keycloak branch,
 * `next-auth/react` is never evaluated in the cloud bundle. Same pattern
 * as `KeycloakAuthShell` / `KeycloakSignInModal`, but expressed with a
 * lazy require because `next/dynamic` only works for components, not
 * hooks.
 */
/* eslint-disable react-hooks/rules-of-hooks --
 * Build-profile dispatch is stable across the app's lifetime; see the
 * JSDoc above for the side-effect failures the unconditional pattern
 * would introduce. */
export function useAuth(): AuthState {
  if (!isKeycloak) return useAuthCloudPath();
  if (isTauri()) return useAuthKeycloakTauriPath();
  // eslint-disable-next-line @typescript-eslint/no-require-imports, global-require -- lazy require so `next-auth/react` (which runs `parseUrl(NEXTAUTH_URL)` at module load) is never imported on cloud builds where NEXTAUTH_URL is unset.
  const { useAuthKeycloakWebPath } = require('./useAuthKeycloakWebPath') as {
    useAuthKeycloakWebPath: typeof UseAuthKeycloakWebPath;
  };
  return useAuthKeycloakWebPath();
}
/* eslint-enable react-hooks/rules-of-hooks */
